import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useLanguage } from './LanguageContext';
import { getSelectionId } from '../utils/formatters';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';

const BettingContext = createContext();

export const useBetting = () => useContext(BettingContext);

export const BettingProvider = ({ children, user, activeCurrency }) => {
    const { t } = useLanguage();
    const [betSlip, setBetSlip] = useState(new Map());

    const addSelection = (match, market, outcome, mode = 'standard') => {
        const id = getSelectionId(match.id, market, outcome);

        setBetSlip(prevSlip => {
            const newSlip = new Map(prevSlip);

            if (newSlip.has(id)) {
                newSlip.delete(id);
                // toast.info(t('common.selection_removed'));
            } else {
                // Removed blocking logic as per request.
                // Conflicts will be handled visually in the BetSlip component.

                const homeName = match.team1 || match.home_team || t('common.home_team');
                const awayName = match.team2 || match.away_team || t('common.away_team');
                const oName = outcome.name.toLowerCase();

                let selectionText = outcome.name;
                const mName = market.name.toLowerCase();

                // Traducción de Selección (Outcome)
                if (mName.includes('1x2') || mName.includes('h2h') || mName.includes('winner') || mName.includes('ganador')) {
                    if (oName === 'home' || oName === '1' || oName === homeName.toLowerCase()) selectionText = homeName;
                    else if (oName === 'away' || oName === '2' || oName === awayName.toLowerCase()) selectionText = awayName;
                    else if (oName === 'draw' || oName === 'x') selectionText = t('common.draw');
                } else {
                    // Traducciones genéricas para otros mercados
                    if (oName.startsWith('over') || oName.startsWith('más')) {
                        selectionText = oName.replace('over', 'Más de').replace('más', 'Más de');
                    } else if (oName.startsWith('under') || oName.startsWith('menos')) {
                        selectionText = oName.replace('under', 'Menos de').replace('menos', 'Menos de');
                    } else if (oName === 'yes' || oName === 'si' || oName === 'sí') {
                        selectionText = 'Sí';
                    } else if (oName === 'no') {
                        selectionText = 'No';
                    } else if (oName.includes('odd') || oName.includes('impar')) {
                        selectionText = 'Impar';
                    } else if (oName.includes('even') || oName.includes('par')) {
                        selectionText = 'Par';
                    }
                }

                // Traducción de Nombre de Mercado (Market Name Display)
                let displayMarketName = market.name;
                if (mName === 'total' || mName.includes('total goals') || mName.includes('over/under')) displayMarketName = 'Total de Goles';
                else if (mName.includes('double chance')) displayMarketName = 'Doble Oportunidad';
                else if (mName.includes('both teams to score')) displayMarketName = 'Ambos Equipos Anotan';
                else if (mName.includes('handicap')) displayMarketName = 'Hándicap';
                else if (mName.includes('correct score')) displayMarketName = 'Marcador Exacto';
                else if (mName.includes('half time/full time')) displayMarketName = 'Descanso / Final';
                else if (mName.includes('odd/even')) displayMarketName = 'Goles Par/Impar';
                else if (mName.includes('clean sheet')) displayMarketName = 'Valla Invicta';
                else if (mName.includes('win to nil')) displayMarketName = 'Ganar a Cero';
                else if (mName.includes('draw no bet')) displayMarketName = 'Empate No Acción';
                else if (mName.includes('corner')) displayMarketName = 'Tiros de Esquina';
                else if (mName.includes('card')) displayMarketName = 'Tarjetas';
                else if (mName.includes('qualif')) displayMarketName = 'Clasificación';
                else if (mName.includes('scorer') || mName.includes('goleador')) displayMarketName = 'Goleadores';
                else if (mName.includes('player') || mName.includes('jugador')) displayMarketName = 'Jugadores';

                newSlip.set(id, {
                    selectionId: id,
                    eventId: match.id,
                    // IDs autoritativos para el backend Zero Trust (re-consulta la cuota en SoftSwiss).
                    matchId: match.id,
                    marketId: market.marketId || market.id,
                    outcomeId: outcome.id,
                    home: homeName,
                    away: awayName,
                    marketName: displayMarketName, // Solo display; la cuota real la fija el servidor
                    selectionText: selectionText,
                    oddValue: outcome.odds, // Solo display/estimación; NO se envía al backend
                    sportKey: match.sport_key || 1,
                    mode: mode // Store the mode
                });
                // toast.success(t('common.added_to_slip'));
            }
            return newSlip;
        });
    };

    // ZERO TRUST: el cliente NO envía cuotas ni ganancias. Solo IDs + monto.
    // El 2º parámetro (customTotalOdds) se ignora a propósito; el servidor es autoritativo.
    const placeBet = async (amount, _ignoredOdds, currencyType, customSelections) => {
        if (!user) {
            toast.info(t('common.login_required'));
            return false;
        }

        // Use custom selections if provided (e.g. System Bet lines), otherwise full slip
        const selectionsToPlace = customSelections || Array.from(betSlip.values());

        if (selectionsToPlace.length === 0) return false;

        // Construir el payload mínimo de IDs. Sin IDs no se puede verificar la cuota.
        const idSelections = selectionsToPlace.map(s => ({
            matchId: s.matchId || s.eventId,
            marketId: s.marketId,
            outcomeId: s.outcomeId,
            sportKey: s.sportKey
        }));

        if (idSelections.some(s => !s.matchId || s.marketId == null || s.outcomeId == null)) {
            toast.error('Selección inválida. Vuelve a elegir la cuota.');
            return false;
        }

        // Mapear billetera: usdt/bonus/bs.
        let betFrom = 'bs';
        if (currencyType) {
            betFrom = currencyType;
        } else {
            betFrom = activeCurrency === 'USDT' ? 'usdt' : (activeCurrency === 'BONUS' ? 'bonus' : 'bs');
        }

        try {
            const fn = httpsCallable(functions, 'placeBet');
            // El backend devuelve la cuota/ganancia REALES calculadas en servidor.
            const { data } = await fn({
                amount: parseFloat(amount),
                betFrom,
                selections: idSelections
            });

            if (!customSelections) {
                setBetSlip(new Map());
            }
            return data || true;
        } catch (e) {
            console.error(e);
            toast.error(e.message || t('common.bet_error'));
            throw e; // Re-throw to let component handle UI state
        }
    };

    return (
        <BettingContext.Provider value={{ betSlip, setBetSlip, addSelection, placeBet }}>
            {children}
        </BettingContext.Provider>
    );
};
